import React from 'react';
import { Target, TrendingUp, AlertCircle, CheckCircle, Clock, DollarSign } from 'lucide-react';
import { formatCurrencyText } from '../../utils/chartFormatters';
import { calculateGoalProgress, calculateMonthsRemaining } from '../../utils/goalCalculations';
import { useCountry } from '../../CountryContext';
import { getCurrencySymbol } from '../../countryConfig';

/**
 * GoalOverlay Component
 * Displays financial goals progress overlaid on wealth tracking dashboard
 */
const GoalOverlay = ({ goals = [], currentNetWorth, snapshots = [], isDark }) => {
  const { selectedCountry } = useCountry();
  const currencySymbol = getCurrencySymbol(selectedCountry);

  // Filter active goals
  const activeGoals = goals.filter(goal => goal.status !== 'completed' && goal.status !== 'cancelled');

  if (activeGoals.length === 0) {
    return null;
  }

  // Calculate trajectory based on recent snapshots
  const calculateTrajectory = (goal) => {
    if (snapshots.length < 2) return null;

    const sorted = [...snapshots].sort((a, b) => a.month.localeCompare(b.month));
    const recentMonths = sorted.slice(-3); // Last 3 months

    if (recentMonths.length < 2) return null;

    const oldestRecent = recentMonths[0];
    const latest = recentMonths[recentMonths.length - 1];

    const monthsElapsed = recentMonths.length;
    const netWorthGrowth = latest.netWorth - oldestRecent.netWorth;
    const avgMonthlyGrowth = netWorthGrowth / monthsElapsed;

    // Project months to reach goal using goal's current amount
    const goalCurrentAmount = parseFloat(goal.currentAmount) || 0;
    const amountNeeded = goal.targetAmount - goalCurrentAmount;
    const monthsToGoal = avgMonthlyGrowth > 0 ? amountNeeded / avgMonthlyGrowth : null;

    return {
      avgMonthlyGrowth,
      monthsToGoal,
      onTrack: monthsToGoal !== null && monthsToGoal <= calculateMonthsRemaining(goal.targetDate)
    };
  };

  const getProgressColor = (progress) => {
    if (progress >= 75) return 'bg-green-500';
    if (progress >= 50) return 'bg-blue-500';
    if (progress >= 25) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getStatusIcon = (trajectory) => {
    if (!trajectory) return null;

    if (trajectory.onTrack) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    } else {
      return <AlertCircle className="w-4 h-4 text-yellow-500" />;
    }
  };

  return (
    <div
      className={`rounded-lg border p-4 sm:p-6 ${
        isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <Target className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Goal Progress Tracking
        </h3>
      </div>

      {/* Goals List */}
      <div className="space-y-4">
        {activeGoals.map((goal) => {
          const goalCurrentAmount = parseFloat(goal.currentAmount) || 0;
          const progress = calculateGoalProgress(goalCurrentAmount, goal.targetAmount);
          const monthsRemaining = calculateMonthsRemaining(goal.targetDate);
          const trajectory = calculateTrajectory(goal);

          return (
            <div
              key={goal.id}
              className={`p-4 rounded-lg border ${
                isDark ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
              }`}
            >
              {/* Goal Header */}
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className={`font-semibold text-sm sm:text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {goal.name || goal.goalType}
                    </h4>
                    {trajectory && getStatusIcon(trajectory)}
                  </div>
                  <div className="flex items-center gap-3 text-xs sm:text-sm">
                    <div className={`flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      <DollarSign className="w-3 h-3" />
                      <span>{formatCurrencyText(goal.targetAmount, currencySymbol)}</span>
                    </div>
                    {monthsRemaining !== null && (
                      <div className={`flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        <Clock className="w-3 h-3" />
                        <span>
                          {monthsRemaining > 0
                            ? `${monthsRemaining} months left`
                            : `${Math.abs(monthsRemaining)} months overdue`}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="text-right">
                  <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {progress.toFixed(0)}%
                  </div>
                  <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Complete
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className={`h-2 rounded-full mb-3 ${isDark ? 'bg-gray-600' : 'bg-gray-200'}`}>
                <div
                  className={`h-full rounded-full transition-all duration-500 ${getProgressColor(progress)}`}
                  style={{ width: `${Math.min(progress, 100)}%` }}
                />
              </div>

              {/* Trajectory Info */}
              {trajectory && snapshots.length >= 2 && (
                <div
                  className={`p-3 rounded-lg ${
                    trajectory.onTrack
                      ? isDark
                        ? 'bg-green-900/20 border border-green-500/30'
                        : 'bg-green-50 border border-green-200'
                      : isDark
                      ? 'bg-yellow-900/20 border border-yellow-500/30'
                      : 'bg-yellow-50 border border-yellow-200'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <TrendingUp
                      className={`w-4 h-4 mt-0.5 ${
                        trajectory.onTrack
                          ? 'text-green-500'
                          : 'text-yellow-500'
                      }`}
                    />
                    <div className="flex-1">
                      <p className={`text-xs sm:text-sm font-semibold mb-1 ${
                        trajectory.onTrack
                          ? isDark ? 'text-green-300' : 'text-green-700'
                          : isDark ? 'text-yellow-300' : 'text-yellow-700'
                      }`}>
                        {trajectory.onTrack ? 'On Track!' : 'Needs Attention'}
                      </p>
                      <p className={`text-xs ${
                        trajectory.onTrack
                          ? isDark ? 'text-green-400' : 'text-green-600'
                          : isDark ? 'text-yellow-400' : 'text-yellow-600'
                      }`}>
                        {trajectory.onTrack
                          ? `At your current pace (${formatCurrencyText(trajectory.avgMonthlyGrowth, currencySymbol)}/month), you'll reach this goal ${
                              trajectory.monthsToGoal
                                ? `in ${Math.round(trajectory.monthsToGoal)} months`
                                : 'on time'
                            }.`
                          : trajectory.avgMonthlyGrowth > 0
                          ? `At your current pace (${formatCurrencyText(trajectory.avgMonthlyGrowth, currencySymbol)}/month), you'll need ${Math.round(trajectory.monthsToGoal)} months - ${Math.round(
                              trajectory.monthsToGoal - monthsRemaining
                            )} months longer than planned.`
                          : 'Your net worth isn\'t growing. Increase contributions or review investment strategy.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Amount Remaining */}
              <div className={`mt-2 text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {goalCurrentAmount < goal.targetAmount ? (
                  <>
                    <span className="font-semibold">{formatCurrencyText(goal.targetAmount - goalCurrentAmount, currencySymbol)}</span>
                    {' '}remaining to reach goal
                  </>
                ) : (
                  <span className="font-semibold text-green-500">Goal Achieved! 🎉</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Goal CTA if no goals */}
      {activeGoals.length === 0 && (
        <div className={`text-center py-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          <Target className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No active financial goals found.</p>
          <p className="text-xs mt-1">Create goals to track progress toward your wealth targets.</p>
        </div>
      )}
    </div>
  );
};

export default GoalOverlay;
